import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/data/Pagination';

import { usePatientsQuery } from '../hooks/use-patients';
import { usePatientListStore } from '../store/patient-list.store';
import {
  PatientListEmpty,
  PatientListSkeleton,
  PatientListTable,
} from './PatientListTable';

interface PatientListViewProps {
  // Slice 5 wires the slide panel here. Until then, row clicks are no-ops.
  onRowClick?: (id: number) => void;
}

function PatientListError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 8,
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span>{t('patientManagement.list.loadError')}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('patientManagement.actions.retry')}
      </button>
    </div>
  );
}

export function PatientListView({ onRowClick }: PatientListViewProps) {
  const filters = usePatientListStore((s) => s.filters);
  const sort = usePatientListStore((s) => s.sort);
  const page = usePatientListStore((s) => s.page);
  const perPage = usePatientListStore((s) => s.perPage);
  const setPage = usePatientListStore((s) => s.setPage);

  // Empty strings are omitted from the wire so the mock/HTTP layer never
  // receives '' (which gets treated as "All").
  const params = {
    q: filters.search.trim() || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    page,
    perPage,
    sortBy: sort.key,
    sortDir: sort.dir,
  };

  const { data, isLoading, error, refetch } = usePatientsQuery(params);

  const isFiltered = Boolean(params.q) || Boolean(params.status);

  if (isLoading) return <PatientListSkeleton />;
  if (error) return <PatientListError onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0)
    return <PatientListEmpty filtered={isFiltered} />;

  const totalPages = Math.max(1, Math.ceil(data.total / data.perPage));

  return (
    <>
      <PatientListTable patients={data.items} onRowClick={onRowClick} />
      <Pagination page={data.page} totalPages={totalPages} onPageChange={setPage} />
    </>
  );
}
